/**
 * reportContentMapper — maps the internal business Report (`ReportPayload`) into the
 * normalized Business Content Model (BCM).
 *
 * This is the MVP's single live source adapter. When external-source mappers
 * (Google, Website, Review, Image, ManualInput) are implemented in Phase 7, each
 * emits a `BusinessContentContribution` that a `BusinessContentMerger` combines;
 * this adapter simply becomes one contributor. It is deliberately the only
 * concrete mapper today, kept in the service layer as pipeline glue — the mapper
 * CONTRACTS live in `domain/mappers/`.
 *
 * Pure function, no side effects. Produces a complete BCM with every section
 * present (empty where unknown) so the planner's input shape is invariant.
 */
import type { ReportPayload } from '../../types'
import type { WebsiteLocale } from '../schema'
import type {
  BusinessContentModel,
  MediaAsset,
  SocialLink,
  SocialPlatform,
  Offering,
  ReviewHighlight,
  PriceLevel,
} from '../domain'

// ── Small pure helpers ──────────────────────────────────────────────────────────

/** Classify a social/profile URL to a known platform by host. */
function classifyPlatform(url: string): SocialPlatform {
  const u = url.toLowerCase()
  if (u.includes('facebook.')) return 'facebook'
  if (u.includes('instagram.')) return 'instagram'
  if (u.includes('tiktok.')) return 'tiktok'
  if (u.includes('youtube.') || u.includes('youtu.be')) return 'youtube'
  if (u.includes('linkedin.')) return 'linkedin'
  if (u.includes('twitter.') || u.includes('x.com')) return 'x'
  if (u.includes('yelp.')) return 'yelp'
  if (u.includes('tripadvisor.')) return 'tripadvisor'
  if (u.includes('wa.me') || u.includes('whatsapp.')) return 'whatsapp'
  return 'other'
}

function toSocialLinks(urls: readonly string[]): SocialLink[] {
  return urls.filter(Boolean).map(url => ({ platform: classifyPlatform(url), url }))
}

function imageAsset(url: string, role: MediaAsset['role']): MediaAsset {
  return { url, kind: 'image', role }
}

function derivePriceLevel(value: number | undefined): PriceLevel | undefined {
  if (value == null || value < 0 || value > 4) return undefined
  return value as PriceLevel
}

/** Normalized capability/amenity tokens (generic, never provider-named). */
function deriveAttributes(report: ReportPayload): string[] {
  const p = report.profile
  if (!p) return []
  const attrs: string[] = []
  if (p.has_ordering) attrs.push('online_ordering')
  if (p.has_reservations) attrs.push('reservations')
  if (p.has_menu) attrs.push('menu')
  if (p.is_verified) attrs.push('verified')
  for (const value of Object.values(p.attributes ?? {})) {
    const token = value?.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '')
    if (token && !attrs.includes(token)) attrs.push(token)
  }
  return attrs
}

/** Generic offerings from capability flags + secondary categories (mock stand-in). */
function deriveOfferings(report: ReportPayload): Offering[] {
  const p = report.profile
  const names: string[] = []
  if (p?.has_ordering) names.push('Online Ordering')
  if (p?.has_reservations) names.push('Reservations')
  if (p?.has_menu) names.push('Menu')
  for (const c of p?.categories ?? []) {
    if (c && c !== p?.category && !names.includes(c)) names.push(c)
  }
  return names.slice(0, 6).map(name => ({ name }))
}

function deriveHighlights(report: ReportPayload): ReviewHighlight[] {
  const reviews = report.reviews?.reviews ?? []
  return reviews
    .filter(r => r.rating >= 4 && r.text.trim().length > 0)
    .slice(0, 6)
    .map(r => ({ text: r.text, author: r.author_name, rating: r.rating, publishedAt: r.published_at }))
}

function deriveTagline(report: ReportPayload): string | undefined {
  const strength = report.ai_summary?.top_strengths?.[0]?.trim()
  if (strength) return strength
  const category = report.profile?.category
  return category ? `${category} • ${report.business_name}` : undefined
}

// ── Report → BCM ─────────────────────────────────────────────────────────────

export function toBusinessContent(report: ReportPayload, locale: WebsiteLocale = 'en'): BusinessContentModel {
  const p = report.profile

  const gallery = (report.photos?.photos ?? [])
    .map(photo => photo.url)
    .filter((url): url is string => Boolean(url))
    .slice(0, 12)
    .map(url => imageAsset(url, 'gallery'))

  const cover = p?.cover_photo_url ? [imageAsset(p.cover_photo_url, 'cover')] : []
  const categories = p?.categories ?? []

  return {
    identity: {
      name: report.business_name || p?.name || '',
      category: p?.category ?? '',
      categories,
      tagline: deriveTagline(report),
      description: (p?.description || report.ai_summary?.executive_summary || '').trim() || undefined,
    },
    location: {
      formattedAddress: p?.address || undefined,
    },
    contact: {
      phone: p?.phone || undefined,
      website: p?.website || undefined,
    },
    brand: {
      logo: p?.profile_photo_url ? imageAsset(p.profile_photo_url, 'logo') : undefined,
      brandColors: [],
      socialLinks: toSocialLinks(p?.social_links ?? []),
    },
    media: {
      cover,
      gallery,
      videos: [],
    },
    reviews: {
      averageRating: p?.rating ?? report.reviews?.average_rating ?? undefined,
      reviewCount: report.reviews?.total_count ?? p?.review_count ?? undefined,
      highlights: deriveHighlights(report),
    },
    offerings: deriveOfferings(report),
    // Business hours are intentionally left empty: the report's hours strings are
    // not reliably parseable, and the normalization rules say "drop, don't guess".
    hours: { regular: [] },
    seo: {
      keywords: [p?.category, ...categories].filter((k): k is string => Boolean(k)),
    },
    trust: {
      awards: [],
      certifications: [],
    },
    attributes: deriveAttributes(report),
    facts: [],
    priceLevel: derivePriceLevel(p?.price_level),
    locale,
  }
}
